import React from 'react';
import {
  Popover, PopoverTrigger, PopoverContent, PopoverBody,
  IconButton, Icon, Flex, Text, Button, Divider, Box, Switch, Tooltip,
  Menu, MenuButton, MenuList, MenuItem,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useDisplayCurrency } from '../../contexts/DisplayCurrencyContext';
import {
  VIEW_LIST, VIEW_BOARD, GROUPINGS, ORDERINGS,
  ROWS_PER_GROUP_OPTIONS, DISPLAY_PROPERTIES, disabledOrderings,
  GROUPING_NONE, GROUPING_DAY, GROUPING_GOAL, subgroupingsFor, canHaveEmptyGroups,
  offeredIn,
} from '../../hooks/useDisplaySettings';
import useGoalsEnabled from '../../hooks/useGoalsEnabled';
import { TOOLBAR_ICON_BUTTON } from '../shared/toolbarButton';

export const SlidersIcon = (props) => (
  <Icon viewBox="0 0 16 16" fill="none" stroke="currentColor" {...props}>
    <path d="M2.5 5h11M2.5 11h11" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="6" cy="5" r="1.8" strokeWidth="1.4" />
    <circle cx="10" cy="11" r="1.8" strokeWidth="1.4" />
  </Icon>
);

export const SectionLabel = ({ children }) => (
  <Text px={1.5} pt={1} pb={1.5} fontSize="11px" fontWeight="600" color="redesign.text.muted"
    textTransform="uppercase" letterSpacing="0.04em">
    {children}
  </Text>
);

export const Row = ({ label, children }) => (
  <Flex align="center" justify="space-between" px={1.5} py={1.5} gap={3}>
    <Text fontSize="xs" color="redesign.text.muted" whiteSpace="nowrap">{label}</Text>
    {children}
  </Flex>
);

/**
 * Every control in this popover.
 *
 * These were native `<select>`s. Ordering had to stop being one: an option can
 * be unavailable — ranking by amount across four currencies means nothing —
 * and saying *why* on the option itself is impossible in a native list, which
 * the operating system draws rather than the page. Once one control was a
 * menu, the rest had to follow: five dropdowns where one looks different is
 * worse than five that share a shape.
 *
 * An unavailable option is styled as disabled rather than actually disabled.
 * A disabled control swallows the mouse events a tooltip listens for, so the
 * explanation would never appear on precisely the options that need it.
 */
export const OptionMenu = ({ value, options, onChange, w = '170px', menuW = '200px' }) => {
  const current = options.find(o => o.value === value);
  return (
    <Menu placement="bottom-end" isLazy>
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon />}
        size="sm"
        h="30px"
        px={3}
        w={w}
        maxW={w}
        fontSize="xs"
        fontWeight="400"
        borderRadius="md"
        textAlign="left"
        bg="redesign.bg.surface"
        border="1px solid"
        borderColor="redesign.border"
        color="redesign.text"
        _hover={{ bg: 'redesign.bg.hover' }}
        _active={{ bg: 'redesign.bg.surface' }}
      >
        <Text fontSize="xs" fontWeight="400" noOfLines={1}>{current?.label ?? ''}</Text>
      </MenuButton>
      <MenuList minW={menuW} py={1}>
        {options.map(option => {
          const why = option.disabledReason;
          const item = (
            <MenuItem
              key={String(option.value)}
              fontSize="xs"
              py={1}
              minH="28px"
              color={why ? 'redesign.text.faint' : 'redesign.text'}
              cursor={why ? 'default' : 'pointer'}
              fontWeight={option.value === value ? '600' : '400'}
              bg={option.value === value ? 'redesign.bg.accent' : 'transparent'}
              _hover={{ bg: why ? 'transparent' : 'redesign.bg.hover' }}
              _focus={{ bg: why ? 'transparent' : 'redesign.bg.hover' }}
              closeOnSelect={!why}
              onClick={why ? undefined : () => onChange(option.value)}
            >
              {option.label}
            </MenuItem>
          );
          return why ? (
            <Tooltip
              key={String(option.value)}
              label={why}
              placement="left"
              openDelay={200}
              hasArrow
              fontSize="xs"
            >
              {item}
            </Tooltip>
          ) : item;
        })}
      </MenuList>
    </Menu>
  );
};

/**
 * MI-139 — the Display popover.
 *
 * Its own toolbar button, deliberately separate from Filter: Filter chooses
 * which movements are in the set, Display chooses how they are presented.
 */
const DisplayMenu = ({ settings, currencyCount = 1, unavailableProperties = [] }) => {
  const { t } = useTranslation('movements');
  const { t: tc } = useTranslation('common');
  // MI-318 — converting is a display choice, so it belongs in this menu rather
  // than in the filter bar: Filter picks which movements are in the set, Display
  // picks how they are shown, and a currency changes the unit, not the data.
  const { displayCurrency, setDisplayCurrency, activeCurrencies } = useDisplayCurrency();
  // Goals is on or off for the whole workspace, so the level and the chip
  // are simply not here where its Owner has it off.
  const goalsEnabled = useGoalsEnabled();
  const offered = (g) => g !== GROUPING_GOAL || goalsEnabled;
  const {
    view, setView, grouping, setGrouping, subgrouping, setSubgrouping,
    ordering, setOrdering, rowsPerGroup, setRowsPerGroup,
    isVisible, toggleProperty, isGrouped,
    showEmptyGroups, setShowEmptyGroups,
    reset, isDefaultDisplay,
  } = settings;

  const disabled = disabledOrderings({
    currencyCount, grouping, subgrouping, converting: Boolean(displayCurrency),
  });

  // MI-326 — Day stays in the list on the board, greyed with the reason, so
  // the reader learns where it went instead of wondering.
  const boardReason = (g) => (offeredIn(view, g) ? null : t('display.grouping.disabled.board'));

  const groupingOptions = GROUPINGS.filter(offered).map(g => ({
    value: g, label: t(`display.grouping.${g}`), disabledReason: boardReason(g),
  }));

  const subgroupingOptions = subgroupingsFor(grouping).filter(offered).map(g => ({
    value: g,
    label: g === GROUPING_NONE
      ? t('display.subgrouping.none')
      : t(`display.grouping.${g}`),
    disabledReason: boardReason(g),
  }));

  const orderingOptions = ORDERINGS.map(o => ({
    value: o,
    label: t(`display.ordering.${o}`),
    disabledReason: disabled[o]
      ? t(`display.ordering.disabled.${disabled[o]}`)
      : null,
  }));

  const currencyOptions = [
    { value: '', label: tc('convert.perCurrency') },
    ...activeCurrencies.map(c => ({ value: c, label: `≈ ${c}` })),
  ];

  const rowsOptions = ROWS_PER_GROUP_OPTIONS.map(n => ({ value: n, label: String(n) }));

  const availableProperties = DISPLAY_PROPERTIES.filter(
    prop => !unavailableProperties.includes(prop)
  );

  return (
    <Popover placement="bottom-end" isLazy>
      <PopoverTrigger>
        <IconButton
          {...TOOLBAR_ICON_BUTTON}
          icon={<SlidersIcon boxSize="14px" />}
          aria-label={t('display.button')}
          title={t('display.button')}
        />
      </PopoverTrigger>
      <PopoverContent w="300px" borderRadius="xl" boxShadow="lg" _focus={{ boxShadow: 'lg' }}>
        <PopoverBody p={2.5}>
          {/* View — List / Board */}
          <Flex bg="redesign.bg.card" borderRadius="md" p="2px" mb={2}>
            {[VIEW_LIST, VIEW_BOARD].map(v => (
              <Button
                key={v}
                flex={1}
                size="xs"
                h="26px"
                fontSize="xs"
                borderRadius="sm"
                variant={view === v ? 'solid' : 'ghost'}
                bg={view === v ? 'redesign.bg.surface' : 'transparent'}
                color={view === v ? 'redesign.text' : 'redesign.text.muted'}
                boxShadow={view === v ? 'xs' : 'none'}
                _hover={{ bg: view === v ? 'redesign.bg.surface' : 'transparent' }}
                onClick={() => setView(v)}
              >
                {t(`display.view.${v}`)}
              </Button>
            ))}
          </Flex>

          <SectionLabel>{t('display.sections.layout')}</SectionLabel>

          <Row label={t('display.grouping.label')}>
            <OptionMenu value={grouping} options={groupingOptions} onChange={setGrouping} />
          </Row>

          {/* A subgroup only means something once there is a group to nest
              under, and it can never repeat the parent's dimension. */}
          {isGrouped && subgroupingOptions.length > 1 && (
            <Row label={t('display.subgrouping.label')}>
              <OptionMenu
                value={subgrouping}
                options={subgroupingOptions}
                onChange={setSubgrouping}
              />
            </Row>
          )}

          <Row label={t('display.ordering.label')}>
            <OptionMenu value={ordering} options={orderingOptions} onChange={setOrdering} />
          </Row>

          {/* Converting needs at least two currencies to mean anything. */}
          {activeCurrencies.length > 1 && (
            <Row label={tc('convert.convertTo')}>
              <OptionMenu
                value={displayCurrency || ''}
                options={currencyOptions}
                onChange={v => setDisplayCurrency(v || null)}
              />
            </Row>
          )}

          {/* Offered for whichever level can report absence — either one, since
              grouping by year and subgrouping by month puts the gaps at the
              second level, which is where they are most often looked for.
              Day is the only dimension that cannot: most days are empty. */}
          {(canHaveEmptyGroups(grouping) || canHaveEmptyGroups(subgrouping)) && (
            <Row label={t('display.emptyGroups.label')}>
              <Switch
                size="sm"
                isChecked={showEmptyGroups}
                onChange={e => setShowEmptyGroups(e.target.checked)}
                aria-label={t('display.emptyGroups.label')}
              />
            </Row>
          )}

          {/* MI-336 — a day group takes all of its rows, so the control would
              be furniture there: day is the default view, and the list it
              replaces never hid a day's eleventh movement. */}
          {isGrouped && grouping !== GROUPING_DAY && (
            <Row label={t('display.rowsPerGroup.label')}>
              <OptionMenu
                value={rowsPerGroup}
                options={rowsOptions}
                onChange={setRowsPerGroup}
                w="90px"
                menuW="90px"
              />
            </Row>
          )}

          <Divider my={2} />

          <SectionLabel>{t('display.properties.label')}</SectionLabel>
          {/* MI-336 — only what the row can actually honour at this width. A
              chip for a column the list has already dropped is a switch that
              does nothing; the property keeps its stored state and its chip
              comes back when the window has room for it again. Removed rather
              than greyed, so the set reads as "what is on offer here" instead
              of a list of disappointments. */}
          <Flex wrap="wrap" gap={1.5} px={1.5} pb={1}>
            {availableProperties.map(prop => {
              const on = isVisible(prop);
              return (
                <Box
                  key={prop}
                  as="button"
                  type="button"
                  onClick={() => toggleProperty(prop)}
                  aria-pressed={on}
                  px={2.5}
                  py={1}
                  borderRadius="full"
                  fontSize="xs"
                  fontWeight="600"
                  border="1px solid"
                  borderColor={on ? 'redesign.accent.blue' : 'redesign.border'}
                  bg={on ? 'redesign.bg.accent' : 'transparent'}
                  color={on ? 'redesign.accent.blue' : 'redesign.text.muted'}
                  _hover={{ borderColor: 'redesign.text.muted' }}
                >
                  {t(`display.properties.${prop}`)}
                </Box>
              );
            })}
          </Flex>

          {!isDefaultDisplay && (
            <>
              <Divider my={2} />
              <Flex justify="flex-end" px={1.5} pb={0.5}>
                <Button size="xs" variant="ghost" fontWeight="500" color="redesign.text.muted"
                  _hover={{ color: 'redesign.text', bg: 'redesign.bg.hover' }} onClick={reset}>
                  {t('display.reset')}
                </Button>
              </Flex>
            </>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default DisplayMenu;
